import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import styles from './SettingsSurface.module.css';

export interface SurfaceConfirmProps {
  ariaLabel: string;
  onDismiss: () => void;
  /** false ⇒ a backdrop tap is ignored (a confirm that must not be dismissed mid-save). */
  dismissOnBackdrop?: boolean;
  testId?: string;
  /** Popup chrome (2026-09-01): centred bold heading with a dialog-width rule under it. Rendered as
   *  the panel's FIRST child so every caller's confirm gets the same head — callers used to write
   *  their own h3 against a per-module .confirmTitle, and the four copies had drifted. */
  title?: ReactNode;
  /** The button row, right-justified under a dialog-width rule, as the panel's LAST child. Buttons
   *  keep their own classes (destructive/primary variants stay caller-owned). */
  footer?: ReactNode;
  children: ReactNode;
}

/**
 * The nested-confirm layer, ALWAYS portalled to document.body. Rendering it in place is a silent
 * bug in the panel presentation (see the .confirmScrim comment in the CSS module). stopPropagation
 * on the backdrop is still required with a portal: React synthetic events bubble through portals to
 * their React ancestors, and the card/sheet root's onClick is onClose.
 *
 * scrim > panel with NOTHING in between: a dozen suites read the confirm's `.parentElement` as its
 * backdrop. title/footer go INSIDE the panel.
 */
export function SurfaceConfirm({ ariaLabel, onDismiss, dismissOnBackdrop = true, testId, title, footer, children }: SurfaceConfirmProps) {
  return createPortal(
    <div className={styles.confirmScrim} data-testid={testId}
      onClick={(e) => { e.stopPropagation(); if (dismissOnBackdrop) onDismiss(); }}>
      <div className={styles.confirmPanel} role="dialog" aria-label={ariaLabel} tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onDismiss(); } }}>
        {title != null && <h3 className={styles.confirmTitle}>{title}</h3>}
        {children}
        {footer != null && <div className={styles.confirmFooter}>{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
